import { useEffect } from "react";
import Notice from "./Notice";
import useDismissableAutohider from "@jnctn/hooks/useDismissableAutohider";
import useOnline from "@jnctn/hooks/useOnline";
import usePreviousValue from "@jnctn/hooks/usePreviousValue";
import useBooleanState from "@jnctn/hooks/useBooleanState";
import StandardButton from "@jnctn/design-system/Button/StandardButton.component";
import { ReactComponent as WifiOffIcon } from "@jnctn/icons/wifi-off.svg";
import { ReactComponent as WifiIcon } from "@jnctn/icons/wifi.svg";
import Text from "@jnctn/design-system/Text/Text.component";

const RECONNECT_DISMISS_DELAY = 4 * 1000;
const OFFLINE_DISMISS_DELAY = 5 * 1000;

export default function OfflineNotice() {
  const rawOnline = useOnline();
  const prevRawOnline = usePreviousValue(rawOnline);
  const [
    showOfflineMessage,
    setShowOfflineMessage,
    setHideOfflineMessage,
  ] = useBooleanState(!rawOnline);
  const [reconnected, setReconnected, setNotReconnected] = useBooleanState(
    false
  );

  // Set reconnected when online transitions to true when previously false
  useEffect(() => {
    if (rawOnline && !prevRawOnline) {
      setReconnected();
    }
    if (rawOnline !== prevRawOnline) {
      if (rawOnline) {
        setHideOfflineMessage();
      } else {
        setShowOfflineMessage();
      }
    }
  }, [
    rawOnline,
    prevRawOnline,
    setHideOfflineMessage,
    setShowOfflineMessage,
    setReconnected,
  ]);

  const [offlineMessageHide] = useDismissableAutohider(
    showOfflineMessage,
    setHideOfflineMessage,
    OFFLINE_DISMISS_DELAY,
    5
  );

  const [restoredMessageHide] = useDismissableAutohider(
    rawOnline && reconnected,
    setNotReconnected,
    RECONNECT_DISMISS_DELAY,
    5
  );

  if (showOfflineMessage) {
    return (
      <Notice
        text="No connection"
        actions={[
          <StandardButton
            label="dismiss"
            size="small"
            type="transparent"
            onClick={offlineMessageHide}
          />,
        ]}
        icon={
          <Text color="secondary" size="body1">
            <WifiOffIcon className="w-10 h-10" />
          </Text>
        }
      />
    );
  }

  if (rawOnline && reconnected) {
    return (
      <Notice
        text="Connection restored"
        actions={[
          <StandardButton
            label="dismiss"
            size="small"
            type="transparent"
            onClick={restoredMessageHide}
          />,
        ]}
        icon={
          <Text color="positive" size="body1">
            <WifiIcon className="w-10 h-10" />
          </Text>
        }
      />
    );
  }

  return null;
}
